import { getAiInferencePresignedUrl } from '../../api/aiInference';
import { resolveRoomObjectImageSrcForDisplay } from '../roomObjectImage';

export const SAVED_RENDER_HISTORY_LIMIT = 8;

/** Max renders persisted to server render history per room image. */
export const SERVER_RENDER_HISTORY_SAVE_LIMIT = 3;

/** Max concurrent room_image_render inference tasks from Export Render. */
export const MAX_CONCURRENT_ROOM_IMAGE_RENDER_TASKS = 3;

function isHttpUrl(value) {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) || trimmed.startsWith('//');
}

function uniqueStrings(values = []) {
  return values.filter((value, index, arr) => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    return arr.findIndex((v) => typeof v === 'string' && v.trim() === trimmed) === index;
  });
}

export function getRenderHistoryItemResultS3Key(item) {
  const values = [
    item?.resultS3Key,
    item?.result_s3_key,
    item?.resultImageS3Key,
    item?.result_image_s3_key,
  ];
  for (const value of values) {
    if (typeof value === 'string' && value.trim() && !isHttpUrl(value)) {
      return value.trim();
    }
  }
  return '';
}

export function normalizeRenderResultS3KeyRef(value) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (isHttpUrl(trimmed)) {
    try {
      const pathname = new URL(trimmed).pathname.replace(/^\//, '');
      return decodeURIComponent(pathname);
    } catch {
      return trimmed;
    }
  }
  return trimmed;
}

export function isRenderResultAlreadyInHistory(localItem, historyItems = []) {
  const localKey = normalizeRenderResultS3KeyRef(
    localItem?.resultS3Key ?? localItem?.result_s3_key ?? '',
  );
  if (!localKey) return false;
  return historyItems.some((entry) => {
    const historyKey = normalizeRenderResultS3KeyRef(getRenderHistoryItemResultS3Key(entry));
    return historyKey && historyKey === localKey;
  });
}

/** Direct image URL candidates from a render-history item (already presigned when applicable). */
export function getRenderHistoryImageCandidates(item) {
  const values = [
    item?.thumbnailUrl,
    item?.thumbnail_url,
    item?.resultUrl,
    item?.result_url,
    item?.imageUrl,
    item?.image_url,
    item?.url,
    item?.imageKey,
    item?.image_key,
    item?.thumbnailImageUrl,
    item?.thumbnail_image_url,
    item?.resultImageUrl,
    item?.result_image_url,
    item?.thumbnailPresignedUrl,
    item?.thumbnail_presigned_url,
    item?.resultPresignedUrl,
    item?.result_presigned_url,
    // Backend often returns presigned URLs in *S3Key fields (same pattern as room image history).
    item?.thumbnailS3Key,
    item?.thumbnail_s3_key,
    item?.resultS3Key,
    item?.result_s3_key,
  ];

  return uniqueStrings(values).filter(isHttpUrl);
}

/** Full-size result URL candidates (preferred for lightbox). */
export function getRenderHistoryResultImageCandidates(item) {
  const values = [
    item?.resultUrl,
    item?.result_url,
    item?.resultImageUrl,
    item?.result_image_url,
    item?.resultPresignedUrl,
    item?.result_presigned_url,
    item?.resultS3Key,
    item?.result_s3_key,
  ];

  return uniqueStrings(values).filter(isHttpUrl);
}

/** Bare S3 object keys that still need presigning via /ai-inference/presigned-url. */
export function getRenderHistoryS3KeyCandidates(item) {
  const values = [
    item?.thumbnailS3Key,
    item?.thumbnail_s3_key,
    item?.resultS3Key,
    item?.result_s3_key,
    item?.resultImageS3Key,
    item?.result_image_s3_key,
  ];

  return uniqueStrings(values).filter((value) => !isHttpUrl(value));
}

export async function resolveRenderHistoryItemImageUrl(item) {
  for (const direct of getRenderHistoryImageCandidates(item)) {
    const displayUrl = resolveRoomObjectImageSrcForDisplay(String(direct).trim());
    if (displayUrl) return displayUrl;
  }

  for (const key of getRenderHistoryS3KeyCandidates(item)) {
    try {
      const presigned = await getAiInferencePresignedUrl(key);
      const rawUrl = presigned?.url ?? presigned?.presignedUrl;
      if (rawUrl) {
        return resolveRoomObjectImageSrcForDisplay(String(rawUrl).trim());
      }
    } catch (err) {
      console.warn('Failed to presign render history image key:', key, err);
    }
  }

  return null;
}

export async function resolveRenderHistoryItemLightboxImageUrl(item) {
  for (const direct of getRenderHistoryResultImageCandidates(item)) {
    const displayUrl = resolveRoomObjectImageSrcForDisplay(String(direct).trim());
    if (displayUrl) return displayUrl;
  }

  const resultKeys = uniqueStrings([
    item?.resultS3Key,
    item?.result_s3_key,
    item?.resultImageS3Key,
    item?.result_image_s3_key,
  ]).filter((value) => !isHttpUrl(value));
  for (const key of resultKeys) {
    try {
      const presigned = await getAiInferencePresignedUrl(key);
      const rawUrl = presigned?.url ?? presigned?.presignedUrl;
      if (rawUrl) {
        return resolveRoomObjectImageSrcForDisplay(String(rawUrl).trim());
      }
    } catch (err) {
      console.warn('Failed to presign render history result key:', key, err);
    }
  }

  return resolveRenderHistoryItemImageUrl(item);
}

export async function presignRenderHistoryS3Keys(item) {
  const presignedUrls = [];
  for (const key of getRenderHistoryS3KeyCandidates(item)) {
    try {
      const presigned = await getAiInferencePresignedUrl(key);
      const rawUrl = presigned?.url ?? presigned?.presignedUrl;
      if (!rawUrl) continue;
      const displayUrl = resolveRoomObjectImageSrcForDisplay(String(rawUrl).trim());
      if (displayUrl) presignedUrls.push(displayUrl);
    } catch (err) {
      console.warn('Failed to presign render history image key:', key, err);
    }
  }
  return presignedUrls;
}

export async function enrichRenderHistoryItems(items = []) {
  return Promise.all(
    items.map(async (item) => {
      const presignedUrls = await presignRenderHistoryS3Keys(item);
      const imageUrl = await resolveRenderHistoryItemImageUrl(item);
      const lightboxImageUrl = await resolveRenderHistoryItemLightboxImageUrl(item);
      const resolvedImageUrl = imageUrl ?? presignedUrls[0] ?? lightboxImageUrl ?? null;
      const resolvedLightboxUrl = lightboxImageUrl ?? imageUrl ?? presignedUrls[0] ?? null;
      const imageUrlCandidates = uniqueStrings([
        ...(resolvedImageUrl ? [resolvedImageUrl] : []),
        ...(resolvedLightboxUrl ? [resolvedLightboxUrl] : []),
        ...presignedUrls,
        ...getRenderHistoryImageCandidates(item).map((url) =>
          resolveRoomObjectImageSrcForDisplay(String(url).trim()),
        ),
      ]);

      return {
        ...item,
        imageUrl: resolvedImageUrl,
        lightboxImageUrl: resolvedLightboxUrl,
        imageUrlCandidates,
      };
    }),
  );
}

export function sortRenderHistoryNewestFirst(items = []) {
  return [...items].sort((a, b) => {
    const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (tb !== ta) return tb - ta;
    return Number(b?.id ?? 0) - Number(a?.id ?? 0);
  });
}

export async function enrichLocalRenderResults(items = []) {
  return Promise.all(
    items.map(async (entry) => {
      const displayItem = {
        ...entry,
        isLocal: true,
        result_s3_key: entry.resultS3Key,
        result_image_s3_key: entry.resultImageS3Key ?? entry.resultS3Key,
        createdAt: entry.completedAt ?? entry.startedAt ?? entry.createdAt,
      };
      const presignedUrls = await presignRenderHistoryS3Keys(displayItem);
      const imageUrl = await resolveRenderHistoryItemImageUrl(displayItem);
      const lightboxImageUrl = await resolveRenderHistoryItemLightboxImageUrl(displayItem);
      const resolvedImageUrl = imageUrl ?? presignedUrls[0] ?? lightboxImageUrl ?? null;
      const resolvedLightboxUrl = lightboxImageUrl ?? imageUrl ?? presignedUrls[0] ?? null;
      const imageUrlCandidates = uniqueStrings([
        ...(resolvedImageUrl ? [resolvedImageUrl] : []),
        ...(resolvedLightboxUrl ? [resolvedLightboxUrl] : []),
        ...presignedUrls,
        ...getRenderHistoryImageCandidates(displayItem).map((url) =>
          resolveRoomObjectImageSrcForDisplay(String(url).trim()),
        ),
      ]);

      return {
        ...displayItem,
        imageUrl: resolvedImageUrl,
        lightboxImageUrl: resolvedLightboxUrl,
        imageUrlCandidates,
      };
    }),
  );
}
